import React, { useState, useRef, useEffect, useCallback } from 'react';

const DateRangePicker = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  placeholder = 'Select date range',
  disabled = false,
  className = '',
  maxDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selecting, setSelecting] = useState('start');
  const [hoverDate, setHoverDate] = useState(null);
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const clearingRef = useRef(false);

  const initMonth = useCallback((dateStr, fallbackOffset) => {
    if (dateStr) {
      const d = new Date(dateStr);
      return { month: d.getMonth(), year: d.getFullYear() };
    }
    const now = new Date();
    let m = now.getMonth() + fallbackOffset;
    let y = now.getFullYear();
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    return { month: m, year: y };
  }, []);

  const [leftMonth, setLeftMonth] = useState(() => initMonth(startDate, -1));
  const [rightMonth, setRightMonth] = useState(() => initMonth(endDate, 0));
  const dropdownRef = useRef(null);

  const parsedStart = startDate ? new Date(startDate + 'T00:00:00') : null;
  const parsedEnd = endDate ? new Date(endDate + 'T00:00:00') : null;
  const parsedMaxDate = maxDate ? new Date(maxDate + 'T00:00:00') : null;

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // dd/mm/yyyy display
  const isoToDisplay = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  // Sync text inputs with prop dates
  useEffect(() => {
    setStartInput(startDate ? isoToDisplay(startDate) : '');
  }, [startDate]);
  useEffect(() => {
    setEndInput(endDate ? isoToDisplay(endDate) : '');
  }, [endDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const getNextMonth = (month, year) => {
    if (month === 11) return { month: 0, year: year + 1 };
    return { month: month + 1, year };
  };
  const getPrevMonth = (month, year) => {
    if (month === 0) return { month: 11, year: year - 1 };
    return { month: month - 1, year };
  };

  const toISODate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Parse dd/mm/yyyy to ISO or null
  const parseTypedDate = (str) => {
    const cleaned = str.replace(/[^0-9/]/g, '');
    const match = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return null;
    const [, dd, mm, yyyy] = match;
    const day = parseInt(dd, 10);
    const month = parseInt(mm, 10);
    const year = parseInt(yyyy, 10);
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return null;
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day > daysInMonth) return null;
    const d = new Date(year, month - 1, day);
    if (parsedMaxDate && d > parsedMaxDate) return null;
    return `${yyyy}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const handleStartInputBlur = () => {
    // Skip validation if we're clearing
    if (clearingRef.current) return;
    if (!startInput) {
      onStartDateChange({ target: { value: '' } });
      onEndDateChange({ target: { value: '' } });
      return;
    }
    const iso = parseTypedDate(startInput);
    if (iso) {
      onStartDateChange({ target: { value: iso } });
      if (endDate && new Date(iso) > new Date(endDate)) {
        onEndDateChange({ target: { value: '' } });
      }
    } else {
      setStartInput(startDate ? isoToDisplay(startDate) : '');
    }
  };

  const handleEndInputBlur = () => {
    if (clearingRef.current) return;
    if (!endInput) {
      onEndDateChange({ target: { value: '' } });
      return;
    }
    const iso = parseTypedDate(endInput);
    if (iso) {
      if (startDate && new Date(iso) < new Date(startDate)) {
        setEndInput(endDate ? isoToDisplay(endDate) : '');
      } else {
        onEndDateChange({ target: { value: iso } });
      }
    } else {
      setEndInput(endDate ? isoToDisplay(endDate) : '');
    }
  };

  const handleStartInputKeyDown = (e) => {
    if (e.key === 'Enter') { e.target.blur(); }
  };
  const handleEndInputKeyDown = (e) => {
    if (e.key === 'Enter') { e.target.blur(); }
  };

  const handleDateClick = (day, month, year) => {
    const clicked = new Date(year, month, day);
    if (parsedMaxDate && clicked > parsedMaxDate) return;

    if (selecting === 'start') {
      const iso = toISODate(clicked);
      onStartDateChange({ target: { value: iso } });
      if (parsedEnd && clicked > parsedEnd) {
        onEndDateChange({ target: { value: '' } });
      }
      setSelecting('end');
    } else {
      if (parsedStart && clicked < parsedStart) {
        const iso = toISODate(clicked);
        onStartDateChange({ target: { value: iso } });
        onEndDateChange({ target: { value: '' } });
        setSelecting('end');
      } else {
        const iso = toISODate(clicked);
        onEndDateChange({ target: { value: iso } });
        setSelecting('start');
        setIsOpen(false);
      }
    }
  };

  const isInRange = (day, month, year) => {
    const d = new Date(year, month, day);
    if (parsedStart && parsedEnd) {
      return d > parsedStart && d < parsedEnd;
    }
    if (parsedStart && !parsedEnd && hoverDate && selecting === 'end') {
      const hd = new Date(hoverDate + 'T00:00:00');
      if (hd >= parsedStart) {
        return d > parsedStart && d < hd;
      }
    }
    return false;
  };

  const isRangeStart = (day, month, year) => {
    if (!parsedStart) return false;
    return parsedStart.getDate() === day && parsedStart.getMonth() === month && parsedStart.getFullYear() === year;
  };

  const isRangeEnd = (day, month, year) => {
    if (!parsedEnd) return false;
    return parsedEnd.getDate() === day && parsedEnd.getMonth() === month && parsedEnd.getFullYear() === year;
  };

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    clearingRef.current = true;
    setStartInput('');
    setEndInput('');
    setSelecting('start');
    setIsOpen(false);
    // Use setTimeout to fire callbacks after blur events are skipped
    setTimeout(() => {
      onStartDateChange({ target: { value: '' } });
      onEndDateChange({ target: { value: '' } });
      clearingRef.current = false;
    }, 0);
  };

  const handleCalendarIconClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (disabled) return;
    if (!isOpen) {
      openCalendar(parsedStart && !parsedEnd ? 'end' : 'start');
    } else {
      setIsOpen(false);
    }
  };

  const navigateMonth = (side, dir) => {
    if (side === 'left') {
      const target = dir === 'prev'
        ? getPrevMonth(leftMonth.month, leftMonth.year)
        : getNextMonth(leftMonth.month, leftMonth.year);
      const targetVal = target.year * 12 + target.month;
      const rightVal = rightMonth.year * 12 + rightMonth.month;
      if (targetVal < rightVal) {
        setLeftMonth(target);
      }
    } else {
      const target = dir === 'prev'
        ? getPrevMonth(rightMonth.month, rightMonth.year)
        : getNextMonth(rightMonth.month, rightMonth.year);
      const targetVal = target.year * 12 + target.month;
      const leftVal = leftMonth.year * 12 + leftMonth.month;
      if (targetVal > leftVal) {
        setRightMonth(target);
      }
    }
  };

  const navigateYear = (side, dir) => {
    if (side === 'left') {
      const target = { month: leftMonth.month, year: leftMonth.year + (dir === 'prev' ? -1 : 1) };
      const targetVal = target.year * 12 + target.month;
      const rightVal = rightMonth.year * 12 + rightMonth.month;
      if (targetVal < rightVal) {
        setLeftMonth(target);
      }
    } else {
      const target = { month: rightMonth.month, year: rightMonth.year + (dir === 'prev' ? -1 : 1) };
      const targetVal = target.year * 12 + target.month;
      const leftVal = leftMonth.year * 12 + leftMonth.month;
      if (targetVal > leftVal) {
        setRightMonth(target);
      }
    }
  };

  const openCalendar = (which) => {
    if (disabled) return;
    setIsOpen(true);
    setSelecting(which);
    if (parsedStart) {
      setLeftMonth({ month: parsedStart.getMonth(), year: parsedStart.getFullYear() });
      if (parsedEnd) {
        if (parsedEnd.getMonth() === parsedStart.getMonth() && parsedEnd.getFullYear() === parsedStart.getFullYear()) {
          const next = getNextMonth(parsedStart.getMonth(), parsedStart.getFullYear());
          setRightMonth(next);
        } else {
          setRightMonth({ month: parsedEnd.getMonth(), year: parsedEnd.getFullYear() });
        }
      } else {
        const next = getNextMonth(parsedStart.getMonth(), parsedStart.getFullYear());
        setRightMonth(next);
      }
    } else {
      const now = new Date();
      const prev = getPrevMonth(now.getMonth(), now.getFullYear());
      setLeftMonth(prev);
      setRightMonth({ month: now.getMonth(), year: now.getFullYear() });
    }
  };

  const renderCalendar = (month, year) => {
    const daysInMonth = getDaysInMonth(month, year);
    const firstDay = getFirstDayOfMonth(month, year);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-7 w-7"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateObj = new Date(year, month, day);
      const isDisabledDate = parsedMaxDate && dateObj > parsedMaxDate;
      const isStart = isRangeStart(day, month, year);
      const isEnd = isRangeEnd(day, month, year);
      const inRange = isInRange(day, month, year);
      const isToday = new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;

      days.push(
        <button
          key={day}
          type="button"
          disabled={isDisabledDate}
          onClick={() => !isDisabledDate && handleDateClick(day, month, year)}
          onMouseEnter={() => setHoverDate(`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`)}
          onMouseLeave={() => setHoverDate(null)}
          className={`h-7 w-7 rounded-md text-xs font-medium transition-all
            ${isDisabledDate ? 'text-gray-300 cursor-not-allowed' :
              isStart || isEnd ? 'bg-primary-500 text-white shadow-md' :
              inRange ? 'bg-primary-100 text-primary-700' :
              isToday ? 'bg-primary-50 text-primary-700 border border-primary-300' :
              'text-gray-700 hover:bg-primary-50 hover:text-primary-700'}
          `}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const hasValue = startDate || endDate;

  const renderMonthHeader = (side, monthData) => (
    <div className="flex items-center justify-between mb-2 bg-primary-500 text-white rounded-lg px-1.5 py-2">
      {/* Left: << (prev year) and < (prev month) */}
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => navigateYear(side, 'prev')}
          className="p-0.5 hover:bg-white/20 rounded transition-all text-[11px] font-bold leading-none"
          title="Previous year"
        >
          «
        </button>
        <button
          type="button"
          onClick={() => navigateMonth(side, 'prev')}
          className="p-0.5 hover:bg-white/20 rounded transition-all text-[11px] font-bold leading-none"
          title="Previous month"
        >
          ‹
        </button>
      </div>

      <span className="font-semibold text-xs">{months[monthData.month]} {monthData.year}</span>

      {/* Right: > (next month) and >> (next year) */}
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => navigateMonth(side, 'next')}
          className="p-0.5 hover:bg-white/20 rounded transition-all text-[11px] font-bold leading-none"
          title="Next month"
        >
          ›
        </button>
        <button
          type="button"
          onClick={() => navigateYear(side, 'next')}
          className="p-0.5 hover:bg-white/20 rounded transition-all text-[11px] font-bold leading-none"
          title="Next year"
        >
          »
        </button>
      </div>
    </div>
  );

  return (
    <div className={`${className}`} ref={dropdownRef} style={{minHeight:'38px'}} >
      <div className="relative">
        {/* Two editable input fields side by side */}
        <div
          className={`
            flex items-center gap-0 w-full
            border border-gray-300 rounded-md shadow-sm bg-white
            transition-all duration-200
            ${isOpen ? 'ring-2 ring-primary-500 border-primary-500' : 'hover:border-primary-300'}
            ${disabled ? 'bg-gray-50 opacity-60 cursor-not-allowed' : ''}
          `}
         style={{minHeight:'38px'}}
        >
          {/* Start date input */}
          <input
            ref={startInputRef}
            type="text"
            value={startInput}
            onChange={(e) => setStartInput(e.target.value)}
            onBlur={handleStartInputBlur}
            onKeyDown={handleStartInputKeyDown}
            onFocus={() => openCalendar('start')}
            disabled={disabled}
            placeholder="dd/mm/yyyy"
            className="w-[88px] pl-2.5 py-2 text-xs bg-transparent border-none outline-none placeholder-gray-400 text-gray-900 font-medium"
          />
          <span className="text-gray-400 text-xs select-none flex-shrink-0">~</span>
          {/* End date input */}
          <input
            ref={endInputRef}
            type="text"
            value={endInput}
            onChange={(e) => setEndInput(e.target.value)}
            onBlur={handleEndInputBlur}
            onKeyDown={handleEndInputKeyDown}
            onFocus={() => openCalendar('end')}
            disabled={disabled}
            placeholder="dd/mm/yyyy"
            className="w-[88px] py-2 text-xs bg-transparent border-none outline-none placeholder-gray-400 text-gray-900 font-medium"
          />

          {/* Show X button when has value and calendar is NOT open; show calendar icon when no value OR calendar is open */}
          {hasValue && !isOpen && !disabled ? (
            <button
              onMouseDown={handleClear}
              className="flex-shrink-0 p-1 mr-1.5 text-gray-400 hover:text-gray-600 focus:outline-none"
              type="button"
              title="Clear dates"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : (
            <button
              onMouseDown={handleCalendarIconClick}
              className="flex-shrink-0 pr-2.5 pl-1 focus:outline-none"
              type="button"
              title="Open calendar"
              disabled={disabled}
            >
              <svg
                className={`w-4 h-4 ${disabled ? 'text-gray-400' : 'text-primary-500'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Dropdown Calendar */}
        {isOpen && (
          <div className="absolute z-[9999] mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-auto">
            {/* Selected range display */}
            <div className="flex items-center justify-center gap-2 mb-2 px-2">
              <div
                onClick={() => { setSelecting('start'); startInputRef.current?.focus(); }}
                className={`flex-1 text-center py-1 px-2 rounded text-xs cursor-pointer border transition-all ${
                  selecting === 'start'
                    ? 'border-primary-500 bg-primary-50 text-primary-700 font-semibold'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                {parsedStart ? isoToDisplay(startDate) : 'Start date'}
              </div>
              <span className="text-gray-400 text-xs">→</span>
              <div
                onClick={() => { setSelecting('end'); endInputRef.current?.focus(); }}
                className={`flex-1 text-center py-1 px-2 rounded text-xs cursor-pointer border transition-all ${
                  selecting === 'end'
                    ? 'border-primary-500 bg-primary-50 text-primary-700 font-semibold'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                {parsedEnd ? isoToDisplay(endDate) : 'End date'}
              </div>
            </div>

            <div className="flex gap-3">
              {/* Left Calendar */}
              <div className="w-56">
                {renderMonthHeader('left', leftMonth)}
                <div className="grid grid-cols-7 gap-0.5 mb-1">
                  {daysOfWeek.map(d => (
                    <div key={d} className="h-7 flex items-center justify-center text-[10px] font-semibold text-gray-500">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {renderCalendar(leftMonth.month, leftMonth.year)}
                </div>
              </div>

              {/* Right Calendar */}
              <div className="w-56">
                {renderMonthHeader('right', rightMonth)}
                <div className="grid grid-cols-7 gap-0.5 mb-1">
                  {daysOfWeek.map(d => (
                    <div key={`r-${d}`} className="h-7 flex items-center justify-center text-[10px] font-semibold text-gray-500">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {renderCalendar(rightMonth.month, rightMonth.year)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangePicker;
